/**
 * ComparisonModal Component
 *
 * Modal for record comparison mode.
 * Contains the preset toolbar, record selector, and comparison views.
 * Responsive: Split view on desktop, stacked view on mobile.
 */

import React, { useState, useCallback } from 'react'
import { Modal, Space, Typography, Divider, Card, Tag, Button, Grid } from 'antd'
import { SwapOutlined, SelectOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useComparisonMode } from './useComparisonMode'
import { ComparisonPresetToolbar } from './ComparisonPresetToolbar'
import { ComparisonRecordSelector } from './ComparisonRecordSelector'
import { ComparisonView } from '@/components/ComparisonView'
import type { ComparisonConfig } from './types'
import type { DisplayMetadata, FieldMetadata, FormSection } from '@/services/metadataService'

const { useBreakpoint } = Grid

const { Text } = Typography

/**
 * Format a record's display title.
 */
function formatRecordTitle(record: Record<string, unknown> | null): string {
  if (!record) return 'No record selected'
  const name = record.name || record.title || record.label || record.uid
  return String(name ?? 'Unnamed')
}

/**
 * Get lifecycle state color.
 */
function getStateColor(state: string | undefined): string {
  if (!state) return 'default'
  const colorMap: Record<string, string> = {
    draft: 'blue',
    active: 'green',
    expired: 'default',
    pending: 'orange',
  }
  return colorMap[state.toLowerCase()] || 'default'
}

/**
 * Record summary card for anchor/comparison display.
 */
function RecordSummaryCard({
  title,
  record,
  stateField,
}: {
  title: string
  record: Record<string, unknown> | null
  stateField?: string
}) {
  if (!record) {
    return (
      <Card size="small" style={{ minWidth: 250, opacity: 0.6 }}>
        <Text type="secondary">{title}</Text>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" italic>
            Select a record to compare
          </Text>
        </div>
      </Card>
    )
  }

  const state = stateField ? (record[stateField] as string | undefined) : undefined
  const effectiveFrom = record.effectiveFrom as string | null
  const effectiveTo = record.effectiveTo as string | null

  return (
    <Card size="small" style={{ minWidth: 250 }}>
      <Text type="secondary">{title}</Text>
      <div style={{ marginTop: 8 }}>
        <Space>
          <Text strong>{formatRecordTitle(record)}</Text>
          {state && <Tag color={getStateColor(state)}>{state}</Tag>}
        </Space>
      </div>
      {(effectiveFrom || effectiveTo) && (
        <div style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {effectiveFrom ? dayjs(effectiveFrom).format('MMM D, YYYY') : '...'}
            {' → '}
            {effectiveTo ? dayjs(effectiveTo).format('MMM D, YYYY') : 'ongoing'}
          </Text>
        </div>
      )}
      {record.lifecycleVersion != null && (
        <div style={{ marginTop: 4 }}>
          <Tag color="blue" style={{ fontSize: 11 }}>
            v{String(record.lifecycleVersion)}
          </Tag>
        </div>
      )}
    </Card>
  )
}

interface ComparisonModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Close modal callback */
  onClose: () => void
  /** API endpoint */
  endpoint: string
  /** Anchor record (record being compared from) */
  anchorRecord: Record<string, unknown> | null
  /** Comparison configuration from metadata */
  comparison: ComparisonConfig
  /** Display metadata */
  display?: DisplayMetadata
  /** Field metadata for comparison view */
  fields: Record<string, FieldMetadata>
  /** Form sections for grouping fields */
  formSections: FormSection[]
}

/**
 * Modal for record comparison mode.
 *
 * Shows preset toolbar for quick selection, record selector for manual picking,
 * and side-by-side comparison view.
 */
export function ComparisonModal({
  open,
  onClose,
  endpoint,
  anchorRecord,
  comparison,
  display,
  fields,
  formSections,
}: ComparisonModalProps): JSX.Element {
  // Use the comparison mode hook
  const {
    isActive,
    comparisonRecord,
    selectorMode,
    activePreset,
    isLoadingPreset,
    lookupDate,
    presetError,
    config,
    enterComparisonMode,
    exitComparisonMode,
    setComparisonRecord,
    setSelectorMode,
    executePreset,
    setLookupDate,
    clearPresetError,
  } = useComparisonMode({
    endpoint,
    config: comparison,
  })

  // Responsive breakpoint detection
  const screens = useBreakpoint()
  const isMobile = !screens.md // Mobile: below md breakpoint (768px)

  // Local state for showing selector
  const [showSelectorPanel, setShowSelectorPanel] = React.useState(false)

  // Local state for differences only toggle
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false)

  // Local state for swap (tracks which record is on which side)
  const [isSwapped, setIsSwapped] = useState(false)

  // Enter comparison mode when modal opens with anchor record
  React.useEffect(() => {
    if (open && anchorRecord && !isActive) {
      enterComparisonMode(anchorRecord)
    }
    if (!open && isActive) {
      exitComparisonMode()
    }
  }, [open, anchorRecord, isActive, enterComparisonMode, exitComparisonMode])

  // Reset swap state when modal closes or records change
  React.useEffect(() => {
    if (!open) {
      setIsSwapped(false)
      setShowDifferencesOnly(false)
    }
  }, [open])

  // Show selector when "pick" preset is selected
  React.useEffect(() => {
    if (activePreset === 'pick') {
      setShowSelectorPanel(true)
    }
  }, [activePreset])

  // Handle modal close
  const handleClose = () => {
    exitComparisonMode()
    setShowSelectorPanel(false)
    onClose()
  }

  // Handle record selection from selector
  const handleSelectRecord = (record: Record<string, unknown>) => {
    setComparisonRecord(record)
    setShowSelectorPanel(false)
  }

  // Handle swap button
  const handleSwap = useCallback(() => {
    setIsSwapped(prev => !prev)
  }, [])

  // Handle differences toggle
  const handleToggleDifferencesOnly = useCallback(() => {
    setShowDifferencesOnly(prev => !prev)
  }, [])

  // Get entity name from display metadata
  const entityName = display?.label_singular || 'Record'

  // Format modal title using config panelTitle template
  const modalTitle = (config?.panelTitle || 'Compare {entity} Versions').replace(
    '{entity}',
    entityName
  )

  // Show selector when "pick" is active, no comparison record, or manually opened
  const showSelector = showSelectorPanel || !comparisonRecord

  // Responsive modal width
  const modalWidth = isMobile ? '100%' : 900

  // Determine left/right records based on swap state
  const leftRecord = isSwapped ? anchorRecord : comparisonRecord
  const rightRecord = isSwapped ? comparisonRecord : anchorRecord

  // Build labels based on record names
  const leftLabel = formatRecordTitle(leftRecord)
  const rightLabel = formatRecordTitle(rightRecord)

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={modalWidth}
      destroyOnHidden
      centered={isMobile}
      style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
      styles={isMobile ? { body: { padding: '12px 16px' } } : undefined}
    >
      {/* Preset Toolbar */}
      <ComparisonPresetToolbar
        presets={config?.presets}
        activePreset={activePreset}
        isLoading={isLoadingPreset}
        error={presetError}
        lookupDate={lookupDate}
        anchorEffectiveFrom={anchorRecord?.effectiveFrom as string | null}
        onPresetSelect={executePreset}
        onDateChange={setLookupDate}
        onClearError={clearPresetError}
        compact={isMobile}
      />

      <Divider style={{ margin: isMobile ? '12px 0' : '16px 0' }} />

      {/* Record Summary Cards - Desktop only */}
      {!isMobile && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <RecordSummaryCard
            title={isSwapped ? 'Anchor' : 'Comparison'}
            record={leftRecord}
            stateField={comparison.enabled ? 'lifecycleState' : undefined}
          />
          <SwapOutlined style={{ fontSize: 24, color: 'var(--rp-blue-primary)' }} />
          <RecordSummaryCard
            title={isSwapped ? 'Comparison' : 'Anchor'}
            record={rightRecord}
            stateField={comparison.enabled ? 'lifecycleState' : undefined}
          />
        </div>
      )}

      {/* Record Selector (when "pick" or no comparison record) */}
      {showSelector && (
        <>
          <Divider style={{ margin: isMobile ? '12px 0' : '16px 0' }}>
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              Select a record to compare
            </Text>
          </Divider>
          <ComparisonRecordSelector
            endpoint={endpoint}
            anchorRecord={anchorRecord}
            selectedRecord={comparisonRecord}
            selectorMode={selectorMode}
            allowModeSwitch={config?.allowSearchMode}
            onSelectRecord={handleSelectRecord}
            onModeChange={setSelectorMode}
            stateOrder={config?.stateOrder}
            maxVersionsEager={config?.maxVersionsEager}
            paginationSize={config?.paginationSize}
            compact={isMobile}
          />
        </>
      )}

      {/* Comparison View - Unified component for desktop and mobile */}
      {!showSelector && (
        <>
          {/* Button to select/change comparison record */}
          <div style={{ marginBottom: isMobile ? 12 : 16, textAlign: 'right' }}>
            <Button
              type="link"
              size={isMobile ? 'small' : 'middle'}
              icon={<SelectOutlined />}
              onClick={() => setShowSelectorPanel(true)}
            >
              {comparisonRecord
                ? (isMobile ? 'Change' : 'Change Comparison Record')
                : (isMobile ? 'Select' : 'Select Comparison Record')
              }
            </Button>
          </div>

          <ComparisonView
            leftRecord={leftRecord}
            rightRecord={rightRecord}
            mode="arbitrary"
            labels={{
              left: leftLabel,
              right: rightLabel,
            }}
            showSwapButton
            onSwap={handleSwap}
            fields={fields}
            formSections={formSections}
            comparison={{
              keyFields: config?.keyFields,
              excludeFields: config?.excludeFields,
            }}
            showDifferencesOnly={showDifferencesOnly}
            onToggleShowDifferencesOnly={handleToggleDifferencesOnly}
            showCopyButton
            maxHeight={isMobile ? '50vh' : '60vh'}
          />
        </>
      )}
    </Modal>
  )
}

export default ComparisonModal
